/**
 * ProceduralPoolGrid — Новая система сетки для процедурных 3D-моделей бассейнов.
 *
 * Все геометрические константы (SCALE_XZ, BASE_CONCRETE_THICKNESS, и т.д.)
 * импортируются из единственного источника правды:
 *   src/features/scene-3d/objects/Pool/config/constants.js → PROC_POOL_GEOMETRY
 *
 * ─── Координаты сцены ───────────────────────────────────────────────────
 *  X → ширина бассейна (Width)
 *  Z → длина бассейна (Length)
 *  Y → высота / глубина (Depth, вниз отрицательная, вверх положительная)
 * ────────────────────────────────────────────────────────────────────────
 */

import { PROC_POOL_GEOMETRY } from '../../objects/Pool/config/constants';

const {
    SCALE_XZ,
    BASE_CONCRETE_THICKNESS,
    WALL_HEIGHT_FACTOR,
    PLASTER_THICKNESS,
    BOTTOM_CONCRETE_THICKNESS,
    POOL_GROUP_Y_OFFSET,
} = PROC_POOL_GEOMETRY;

/**
 * Рассчитать идентичные геометрии параметры процедурных бассейнов.
 *
 * @param {number} length  Длина бассейна из UI (в метрах, ось Z)
 * @param {number} width   Ширина бассейна из UI (в метрах, ось X)
 * @param {number} depth   Глубина бассейна из UI (в метрах)
 * @returns {ProceduralPoolDims}
 */
export function calculateProceduralPoolDims(length, width, depth) {
    const L = Math.max(0.1, Number(length) || 4);
    const W = Math.max(0.1, Number(width) || 4);
    const D = Math.max(0.1, Number(depth) || 1.5);

    // Видимые (scaled) горизонтальные размеры — идентично моделям
    const LVis = L * SCALE_XZ;
    const WVis = W * SCALE_XZ;

    // Внутренняя чаша (стены, на которые нанесена отделка)
    const innerX = WVis / 2 - BASE_CONCRETE_THICKNESS;
    const innerZ = LVis / 2 - BASE_CONCRETE_THICKNESS;

    // Поверх отделки — координата, которую ВИДИТ пользователь внутри
    const surfaceX = innerX + PLASTER_THICKNESS;
    const surfaceZ = innerZ + PLASTER_THICKNESS;

    // Высота стены в мировых координатах
    const wallHeight = D * WALL_HEIGHT_FACTOR;

    // Y-центр стен (середина высоты стены над дном)
    const wallCenterY = POOL_GROUP_Y_OFFSET + BOTTOM_CONCRETE_THICKNESS + wallHeight / 2;

    // Y-поверхности дна (отделка)
    const floorY = POOL_GROUP_Y_OFFSET + BOTTOM_CONCRETE_THICKNESS + PLASTER_THICKNESS / 2;

    return {
        innerHalfX: surfaceX,
        innerHalfZ: surfaceZ,
        innerWidth: surfaceX * 2,
        innerLength: surfaceZ * 2,
        wallHeight,
        wallCenterY,
        floorY,
        rawLength: L,
        rawWidth: W,
        rawDepth: D,
    };
}

// --------------------------------------------------------------------------
// Идентификаторы поверхностей
// --------------------------------------------------------------------------
export const PROC_SURFACE_IDS = {
    WALL_LEFT: 'WALL_LEFT',
    WALL_RIGHT: 'WALL_RIGHT',
    WALL_FRONT: 'WALL_FRONT',
    WALL_BACK: 'WALL_BACK',
    FLOOR: 'FLOOR',
};

/**
 * Построить описания поверхностей по рассчитанным размерам.
 *
 * @param {ReturnType<calculateProceduralPoolDims>} dims
 */
export function buildProcSurfaces(dims) {
    const { innerHalfX, innerHalfZ, innerWidth, innerLength, wallCenterY, floorY } = dims;

    return {
        [PROC_SURFACE_IDS.WALL_LEFT]: {
            id: PROC_SURFACE_IDS.WALL_LEFT,
            constantAxis: 'x',
            constantCoord: -innerHalfX,
            variableAxis: 'z',
            sizeMain: innerLength,
            centerY: wallCenterY,
            normal: [1, 0, 0],
            rotation: [0, Math.PI / 2, 0],
        },
        [PROC_SURFACE_IDS.WALL_RIGHT]: {
            id: PROC_SURFACE_IDS.WALL_RIGHT,
            constantAxis: 'x',
            constantCoord: innerHalfX,
            variableAxis: 'z',
            sizeMain: innerLength,
            centerY: wallCenterY,
            normal: [-1, 0, 0],
            rotation: [0, -Math.PI / 2, 0],
        },
        [PROC_SURFACE_IDS.WALL_FRONT]: {
            id: PROC_SURFACE_IDS.WALL_FRONT,
            constantAxis: 'z',
            constantCoord: -innerHalfZ,
            variableAxis: 'x',
            sizeMain: innerWidth,
            centerY: wallCenterY,
            normal: [0, 0, 1],
            rotation: [0, 0, 0],
        },
        [PROC_SURFACE_IDS.WALL_BACK]: {
            id: PROC_SURFACE_IDS.WALL_BACK,
            constantAxis: 'z',
            constantCoord: innerHalfZ,
            variableAxis: 'x',
            sizeMain: innerWidth,
            centerY: wallCenterY,
            normal: [0, 0, -1],
            rotation: [0, Math.PI, 0],
        },
        [PROC_SURFACE_IDS.FLOOR]: {
            id: PROC_SURFACE_IDS.FLOOR,
            constantAxis: 'y',
            constantCoord: floorY,
            variableAxis: 'xz',
            sizeMain: innerWidth,
            sizeCross: innerLength,
            centerY: floorY,
            normal: [0, 1, 0],
            rotation: [-Math.PI / 2, 0, 0],
        },
    };
}

/**
 * Равномерно распределить N точек на одной поверхности (equal_gaps).
 *
 * @param {ProcSurface} surface
 * @param {number} count
 * @param {{ padding?: number, offset?: number, fixedY?: number }} options
 */
export function distributeOnSurface(surface, count, options = {}) {
    if (!surface || count <= 0) return [];

    const { padding = 0, offset = 0, fixedY = null } = options;
    const effectiveSize = Math.max(0, surface.sizeMain - 2 * padding);
    const posY = fixedY !== null ? fixedY : surface.centerY;

    const points = [];

    for (let i = 0; i < count; i++) {
        const t = (i + 1) / (count + 1);
        const posMain = -effectiveSize / 2 + t * effectiveSize;

        const point = {
            x: 0,
            y: posY,
            z: 0,
            rotation: surface.rotation,
            normal: surface.normal,
            surfaceId: surface.id,
        };

        if (surface.variableAxis === 'z') {
            point.z = posMain;
            point.x = surface.constantCoord + surface.normal[0] * offset;
        } else if (surface.variableAxis === 'x') {
            point.x = posMain;
            point.z = surface.constantCoord + surface.normal[2] * offset;
        }

        point.position = [point.x, point.y, point.z];
        points.push(point);
    }

    return points;
}

/** ~5% от стороны дна, min 4 см и max 15% (в координатах сцены). */
const FLOOR_INSET_RATIO = 0.01;
const FLOOR_INSET_MAX_RATIO = 0.20;
const FLOOR_INSET_MIN_SCENE = 0.02 * SCALE_XZ;

/**
 * Отступ от края дна по одной оси (ширина или длина отдельно).
 * @param {number} innerSize
 * @returns {number}
 */
function resolveFloorInset(innerSize) {
    const half = innerSize / 2;
    if (half <= FLOOR_INSET_MIN_SCENE) return 0;
    const byRatio = innerSize * FLOOR_INSET_RATIO;
    const clamped = Math.min(byRatio, innerSize * FLOOR_INSET_MAX_RATIO);
    return Math.max(FLOOR_INSET_MIN_SCENE, Math.min(clamped, half - 0.04));
}

/**
 * Подобрать строки/столбцы сетки на дне под пропорции чаши.
 * @param {number} count
 * @param {number} usableW
 * @param {number} usableL
 * @returns {{ rows: number, cols: number, pattern?: string }}
 */
function computeFloorGridLayout(count, usableW, usableL) {
    if (count <= 0) return { rows: 0, cols: 0 };
    if (count === 1) return { rows: 1, cols: 1 };

    if (count === 2) {
        return { rows: 2, cols: 2, pattern: 'diagonal' };
    }
    if (count === 3) {
        return { rows: 2, cols: 2, pattern: 'triangle' };
    }

    const aspect = usableW / Math.max(0.1, usableL);
    let cols = Math.max(2, Math.ceil(Math.sqrt(count * aspect)));
    let rows = Math.ceil(count / cols);

    while (rows * cols < count) {
        cols += 1;
        rows = Math.ceil(count / cols);
    }

    while (rows > 2 && (rows - 1) * cols >= count) {
        rows -= 1;
    }
    while (cols > 2 && rows * (cols - 1) >= count) {
        cols -= 1;
    }

    if (rows === 1 && cols < count) {
        rows = Math.min(count, Math.max(2, Math.ceil(count / cols)));
    }
    if (cols === 1 && rows < count) {
        cols = Math.min(count, Math.max(2, Math.ceil(count / rows)));
    }

    return { rows, cols };
}

/**
 * Индексы ячеек сетки для нестандартных раскладок (2 и 3 форсунки).
 * @param {number} count
 * @param {number} rows
 * @param {number} cols
 * @param {string|undefined} pattern
 * @returns {Array<{ row: number, col: number }>}
 */
function resolveFloorCellIndices(count, rows, cols, pattern) {
    if (pattern === 'diagonal' && count === 2) {
        return [{ row: 0, col: 0 }, { row: 1, col: 1 }];
    }
    if (pattern === 'triangle' && count === 3) {
        return [{ row: 0, col: 0 }, { row: 0, col: 1 }, { row: 1, col: 0 }];
    }

    const cells = [];
    for (let i = 0; i < count; i++) {
        cells.push({ row: Math.floor(i / cols), col: i % cols });
    }
    return cells;
}

/**
 * Координата по оси с равными зазорами от краёв (как distributeOnSurface).
 * @param {number} index
 * @param {number} total
 * @param {number} halfSpan — половина usable (от центра до края)
 * @returns {number}
 */
function floorAxisCoord(index, total, halfSpan) {
    if (total <= 1) return 0;
    const t = (index + 1) / (total + 1);
    return -halfSpan + t * (halfSpan * 2);
}

/**
 * Равномерное распределение точек по всей площади дна (2D, equal_gaps).
 * @param {ReturnType<calculateProceduralPoolDims>} dims
 * @param {number} count
 * @param {{ padding?: number }} [options]
 * @returns {Array}
 */
export function distributeOnFloor(dims, count, options = {}) {
    if (!dims || count <= 0) return [];

    const { innerWidth, innerLength, floorY } = dims;
    const paddingX =
        options.padding != null
            ? Math.min(options.padding, innerWidth / 2 - 0.04)
            : resolveFloorInset(innerWidth);
    const paddingZ =
        options.padding != null
            ? Math.min(options.padding, innerLength / 2 - 0.04)
            : resolveFloorInset(innerLength);

    const usableW = Math.max(0.12, innerWidth - paddingX * 2);
    const usableL = Math.max(0.12, innerLength - paddingZ * 2);
    const halfW = usableW / 2;
    const halfL = usableL / 2;

    const { rows, cols, pattern } = computeFloorGridLayout(count, usableW, usableL);
    const cells = resolveFloorCellIndices(count, rows, cols, pattern);

    return cells.map(({ row, col }) => {
        const x = floorAxisCoord(col, cols, halfW);
        const z = floorAxisCoord(row, rows, halfL);

        return {
            x,
            y: floorY,
            z,
            position: [x, floorY, z],
            rotation: [-Math.PI / 2, 0, 0],
            normal: [0, 1, 0],
            surfaceId: PROC_SURFACE_IDS.FLOOR,
        };
    });
}

/**
 * Главный класс — единая точка входа.
 * API совместим с устаревшей PoolGridSystem.
 */
export class ProceduralPoolGrid {
    constructor(length, width, depth, poolType = 'скиммерный') {
        this.dims = calculateProceduralPoolDims(length, width, depth, poolType);
        this.surfaces = buildProcSurfaces(this.dims);
    }

    distributeEvenly(surfaceId, count, options = {}) {
        const surface = this.surfaces[surfaceId];
        return distributeOnSurface(surface, count, options);
    }

    /** Равномерная сетка на дне чаши */
    distributeOnFloor(count, options = {}) {
        return distributeOnFloor(this.dims, count, options);
    }
}
